import { mapAvailabilityForRegistrationPost } from '@/helpers/availabilityHelpers'
import { hasOperationsDepartment } from '@/helpers/conditionalFormFieldHelpers'
import { Address } from '@/types/addresses'
import {
    RegistrationCheckFormModel,
    RegistrationContactExternalSigningModel,
    RegistrationFormKeys,
    RegistrationFormModel,
    RegistrationPostModel,
} from '@/types/registration-data'
import { Contact, RegistrationAction } from '@/types/registration'
import {
    RegistrationAddressModel,
    RegistrationContactModel,
    RegistrationSubmittedIdsResult,
} from '@/types/__generated__/registration'
import { DepartmentType } from '@/types/questions'
import { createContext, Dispatch, ReactNode, SetStateAction, use, useState, ReactElement } from 'react'
import { LocaleContext } from '../locale-provider'

export interface RegistrationLine {
    registrationKey: string
    endpointKey: string
    startDate: Date
}

export interface CompanyActivity {
    contractKey: string
    startDate: Date
}

interface RegistrationDataProviderValue {
    registrationLines: RegistrationLine[]
    updateRegistrationLines?: (registrationLines: RegistrationLine[]) => void
    registrationFormData: RegistrationFormModel
    updateRegistrationFormData?: (registrationFormData: RegistrationFormModel) => void
    updateRegistrationFormDataWithExternalSigning?: (
        registrationFormData: RegistrationContactExternalSigningModel,
    ) => void
    getMappedRegistrationData?: (companyActivities: CompanyActivity[]) => RegistrationPostModel
    clearRegistrationFormData?: () => void
    setIsFormDirty?: Dispatch<SetStateAction<boolean>>
    isFormDirty: boolean
    requiresAvailability: boolean
    requiresPickupAddress: boolean
    registrationId: RegistrationSubmittedIdsResult
    setRegistrationId?: (id: RegistrationSubmittedIdsResult) => void
    setNonContractRegistrationKeys?: (value: string[]) => void
    setRegistrationKeys?: (value: string[]) => void
    setDepartmentTypes?: (value: DepartmentType[]) => void
    registrationKeys?: string[]
    departmentTypes: DepartmentType[]
    /**
     * A 'non contract registration key' is a trigger for the back end to do *something*
     * (like set a boolean in the settings) for the registering company,
     * but not create a contract
     **/
    nonContractRegistrationKeys: string[]
    registrationCheckAnswers: RegistrationCheckFormModel[]
    setRegistrationCheckAnswers: (registrationCheckAnswers: RegistrationCheckFormModel[]) => void
}

export const RegistrationDataContext = createContext<RegistrationDataProviderValue>({
    registrationLines: [],
    nonContractRegistrationKeys: [],
    registrationFormData: null,
    isFormDirty: false,
    requiresAvailability: true,
    requiresPickupAddress: true,
    registrationId: null,
    setRegistrationKeys: () => {},
    setDepartmentTypes: () => {},
    registrationKeys: [],
    departmentTypes: [],
    registrationCheckAnswers: [],
    setRegistrationCheckAnswers: () => {},
})

interface RegistrationDataProviderProps {
    children: ReactNode
}

export const RegistrationDataProvider = ({ children }: RegistrationDataProviderProps): ReactElement => {
    const { activeLocale } = use(LocaleContext)
    const [registrationId, setRegistrationId] = useState<RegistrationSubmittedIdsResult>(null)
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false)
    const [registrationLines, setRegistrationLines] = useState<RegistrationLine[]>([])
    const [nonContractRegistrationKeys, setNonContractRegistrationKeys] = useState<string[]>([])
    const [registrationKeys, setRegistrationKeys] = useState<string[]>([])
    const [departmentTypes, setDepartmentTypes] = useState<DepartmentType[]>([])
    const [registrationCheckAnswers, setRegistrationCheckAnswers] = useState<RegistrationCheckFormModel[]>([])
    const [registrationFormData, setRegistrationFormData] = useState<RegistrationFormModel>(null)

    const requiresAvailability = hasOperationsDepartment(departmentTypes)
    const requiresPickupAddress = hasOperationsDepartment(departmentTypes)

    const updateRegistrationLines = (newRegistrationLines: RegistrationLine[]): void => {
        setRegistrationLines(newRegistrationLines)
    }

    const updateRegistrationFormData = (updatedRegistrationFormData: RegistrationFormModel): void => {
        setRegistrationFormData({
            ...updatedRegistrationFormData,
            [RegistrationFormKeys.REGISTRATION_CHECK_ANSWERS]: registrationCheckAnswers.map((answer) => answer.answers),
        })
    }

    const updateRegistrationFormDataWithExternalSigning = (
        externalSigningFormData: RegistrationContactExternalSigningModel,
    ): void => {
        setRegistrationFormData({ ...registrationFormData, ...externalSigningFormData })
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getFormValueToPost = (key: RegistrationFormKeys): any | null => {
        const value = registrationFormData[key]
        if (typeof value !== 'string') return value

        return value === '' ? null : value
    }

    const getMappedRegistrationData = (companyActivities: CompanyActivity[]): RegistrationPostModel => {
        if (!registrationFormData) return

        const addresses: RegistrationAddressModel[] = [
            {
                postalCode: getFormValueToPost(RegistrationFormKeys.POSTAL_CODE),
                number: getFormValueToPost(RegistrationFormKeys.HOUSE_NUMBER),
                suffix: getFormValueToPost(RegistrationFormKeys.HOUSE_NUMBER_SUFFIX),
                street: getFormValueToPost(RegistrationFormKeys.STREET),
                city: getFormValueToPost(RegistrationFormKeys.CITY),
                countryCode: getFormValueToPost(RegistrationFormKeys.COUNTRY_CODE),
                countryName: getFormValueToPost(RegistrationFormKeys.COUNTRY_FULL_NAME),
                remarks: getFormValueToPost(RegistrationFormKeys.REMARKS),
                addressType: Address.MAIN,
            },
        ]

        if (registrationFormData[RegistrationFormKeys.PICKUP_POSTAL_CODE]) {
            addresses.push({
                postalCode: getFormValueToPost(RegistrationFormKeys.PICKUP_POSTAL_CODE),
                number: getFormValueToPost(RegistrationFormKeys.PICKUP_HOUSE_NUMBER),
                suffix: getFormValueToPost(RegistrationFormKeys.PICKUP_HOUSE_NUMBER_SUFFIX),
                street: getFormValueToPost(RegistrationFormKeys.PICKUP_STREET),
                city: getFormValueToPost(RegistrationFormKeys.PICKUP_CITY),
                countryCode: getFormValueToPost(RegistrationFormKeys.PICKUP_COUNTRY_CODE),
                countryName: getFormValueToPost(RegistrationFormKeys.PICKUP_COUNTRY_FULL_NAME),
                remarks: getFormValueToPost(RegistrationFormKeys.PICKUP_REMARKS),
                addressType: Address.PICKUP,
            })
        }

        const contacts: RegistrationContactModel[] = []
        if (getFormValueToPost(RegistrationFormKeys.HAS_CONTACT_PERSON) !== false) {
            contacts.push({
                firstName: getFormValueToPost(RegistrationFormKeys.FIRST_NAME),
                prefixLastName: getFormValueToPost(RegistrationFormKeys.MIDDLE_NAME),
                lastName: getFormValueToPost(RegistrationFormKeys.LAST_NAME),
                primaryPhoneNumber: getFormValueToPost(RegistrationFormKeys.PRIMARY_PHONE),
                secondaryPhoneNumber: getFormValueToPost(RegistrationFormKeys.SECONDARY_PHONE),
                email: getFormValueToPost(RegistrationFormKeys.EMAIL),
                jobTitle: getFormValueToPost(RegistrationFormKeys.JOB_TITLE),
                type: Contact.DEFAULT,
                locale: activeLocale,
            })
        }

        if (registrationFormData[RegistrationFormKeys.EXTERNAL_SIGNING_FIRST_NAME]) {
            contacts.push({
                firstName: getFormValueToPost(RegistrationFormKeys.EXTERNAL_SIGNING_FIRST_NAME),
                prefixLastName: getFormValueToPost(RegistrationFormKeys.EXTERNAL_SIGNING_MIDDLE_NAME),
                lastName: getFormValueToPost(RegistrationFormKeys.EXTERNAL_SIGNING_LAST_NAME),
                email: getFormValueToPost(RegistrationFormKeys.EXTERNAL_SIGNING_EMAIL),
                jobTitle: getFormValueToPost(RegistrationFormKeys.EXTERNAL_SIGNING_JOB_TITLE),
                type: Contact.EXTERNAL_SIGNING,
                locale: activeLocale,
            })
        }

        return {
            companyName: getFormValueToPost(RegistrationFormKeys.COMPANY_NAME),
            commerceNumber: getFormValueToPost(RegistrationFormKeys.CHAMBER_OF_COMMERCE_NUMBER),
            website: getFormValueToPost(RegistrationFormKeys.WEBSITE),
            companyPhoneNumber: getFormValueToPost(RegistrationFormKeys.PHONE_NUMBER),
            companyChain: getFormValueToPost(RegistrationFormKeys.CHAIN),
            companyChainName: getFormValueToPost(RegistrationFormKeys.CHAIN_FULL_NAME),
            addresses,
            contacts,
            ibanNumber: getFormValueToPost(RegistrationFormKeys.IBAN),
            bicSwiftNumber: getFormValueToPost(RegistrationFormKeys.BIC_SWIFT),
            vatNumber: getFormValueToPost(RegistrationFormKeys.VAT_NUMBER),
            invoiceEmail: getFormValueToPost(RegistrationFormKeys.INVOICE_EMAIL),
            invoiceLocale: getFormValueToPost(RegistrationFormKeys.INVOICE_LOCALE),
            openingHours: requiresAvailability
                ? mapAvailabilityForRegistrationPost(registrationFormData)
                : [],
            companyActivities,
            action: registrationFormData[RegistrationFormKeys.HAS_EXTERNAL_SIGNER]
                ? RegistrationAction.EXTERNAL_SIGNING
                : RegistrationAction.SUBMIT,
            complianceCompanyCode: getFormValueToPost(RegistrationFormKeys.COMPLIANCE_COMPANY_CODE) ?? null,
            complianceCompanyName: getFormValueToPost(RegistrationFormKeys.COMPLIANCE_COMPANY_NAME) ?? null,
            registrationAnswers: registrationCheckAnswers.map((answer) => answer.answers),
        }
    }

    const clearRegistrationFormData = (): void => setRegistrationFormData(null)
    const setId = (id: RegistrationSubmittedIdsResult): void => setRegistrationId(id)

    const providerValue: RegistrationDataProviderValue = {
        // registration keys
        registrationLines,
        updateRegistrationLines,
        setNonContractRegistrationKeys,
        nonContractRegistrationKeys,
        registrationKeys,
        setRegistrationKeys,
        departmentTypes,
        setDepartmentTypes,

        // registration form
        registrationFormData,
        updateRegistrationFormData,
        updateRegistrationFormDataWithExternalSigning,
        getMappedRegistrationData,
        clearRegistrationFormData,
        setIsFormDirty,
        isFormDirty,
        requiresAvailability,
        requiresPickupAddress,

        // registration id
        registrationId,
        setRegistrationId: setId,

        // registration check answers
        registrationCheckAnswers,
        setRegistrationCheckAnswers,
    }

    return <RegistrationDataContext.Provider value={providerValue}>{children}</RegistrationDataContext.Provider>
}
